import { useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import type { ContactItem } from '~hooks/useContact/models/ContactItem';
import type { WayPoint } from '~hooks/useSites/models';
import { useSites } from '~hooks/useSites/useSites';

import type {
  OrderFormV3FieldValues,
  OrderWaypointDisplayValue,
  OrderWaypointSiteValue,
} from '../OrderFormV3Context';

type SetWaypointDisplay = (
  update: (previous: OrderWaypointDisplayValue) => OrderWaypointDisplayValue,
) => void;

/**
 * Triggers: the watched `pickUpWayPoint.site` / `dropOffWayPoint.site` ids
 *           (via the Provider), the default waypoints' contact name /
 *           discarded flag, and the shared post-mount `loaded` latch.
 * Writes:   `pickUpWayPoint.contact` / `dropOffWayPoint.contact` form values
 *           (first site contact), and through the host: the Provider's
 *           contact lists (autocomplete options), the show-contact flags
 *           (auto-open), the lastSelected*Phone remount keys, and the
 *           waypoint display values (contact merge).
 * Guards:   never fires before the `loaded` latch flips (mount settles
 *           first); skipped when the default waypoint already carries a
 *           contact name (saved contacts win); skipped on edit re-open for
 *           the INITIALLY-LOADED saved site, so a contact the dispatcher
 *           removed (persisted as empty) is not resurrected — only a
 *           genuine site change auto-fills (TRE-15128; legacy re-populated it
 *           unconditionally); the fetch itself requires a site id, a
 *           non-discarded default site (legacy keys this off the MOUNT-TIME
 *           default waypoint's site) and a selected company.
 * Legacy:   the legacy form's pickup and dropoff site-contact autofill
 *           effects and its setSiteContact helper.
 * Test:     __tests__/OrderFormV3Context.test.tsx
 *           ("site-contact auto-population").
 */
export function useWaypointContactAutofill({
  loaded,
  watchedCompanyId,
  watchedPickUpWaypointSite,
  watchedDropOffWaypointSite,
  defaultPickUpWaypoint,
  defaultDropOffWaypoint,
  isEditingOrder,
  setPickUpContacts,
  setDropOffContacts,
  setShowPickUpContact,
  setShowDropOffContact,
  setLastSelectedPickUpPhone,
  setLastSelectedDropOffPhone,
  setPickUpWaypoint,
  setDropOffWaypoint,
  setValue,
}: {
  loaded: boolean;
  watchedCompanyId: string | undefined;
  watchedPickUpWaypointSite: OrderWaypointSiteValue;
  watchedDropOffWaypointSite: OrderWaypointSiteValue;
  defaultPickUpWaypoint: WayPoint | undefined;
  defaultDropOffWaypoint: WayPoint | undefined;
  isEditingOrder: boolean;
  setPickUpContacts: (contacts: ContactItem[]) => void;
  setDropOffContacts: (contacts: ContactItem[]) => void;
  setShowPickUpContact: (show: boolean) => void;
  setShowDropOffContact: (show: boolean) => void;
  setLastSelectedPickUpPhone: (phone: string) => void;
  setLastSelectedDropOffPhone: (phone: string) => void;
  setPickUpWaypoint: SetWaypointDisplay;
  setDropOffWaypoint: SetWaypointDisplay;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  const { getSiteContacts } = useSites();

  // Split per waypoint so each form write keeps a literal path. The display
  // merge is the legacy `{ ...waypoint, contact }` spread as a functional
  // update.
  const setSiteContact = async (
    waypointType: 'pickUpWayPoint' | 'dropOffWayPoint',
    siteId?: string,
    isDiscarded?: boolean,
  ) => {
    if (siteId && !isDiscarded && watchedCompanyId) {
      const siteContacts = await getSiteContacts({
        companyId: watchedCompanyId,
        siteId,
      });

      if (siteContacts.length > 0) {
        const contact = {
          name: siteContacts[0].name,
          phone: siteContacts[0].phone,
        };
        if (waypointType === 'pickUpWayPoint') {
          setPickUpContacts(siteContacts);
          setShowPickUpContact(true);
          setLastSelectedPickUpPhone(siteContacts[0].phone || '');
          setPickUpWaypoint((previous) => ({ ...previous, contact }));
          setValue('pickUpWayPoint.contact', contact, { shouldDirty: false });
        } else {
          setDropOffContacts(siteContacts);
          setShowDropOffContact(true);
          setLastSelectedDropOffPhone(siteContacts[0].phone || '');
          setDropOffWaypoint((previous) => ({ ...previous, contact }));
          setValue('dropOffWayPoint.contact', contact, { shouldDirty: false });
        }
      }
    }
  };

  useEffect(() => {
    // TRE-15128: on edit re-open the saved order's pickup contact is
    // authoritative — including one the dispatcher REMOVED (persisted as an
    // empty contact). Refetching the unchanged saved site's contact would
    // resurrect it, so suppress the autofill for the initially-loaded saved
    // site; selecting a DIFFERENT site still auto-fills.
    const isInitialSavedSite =
      isEditingOrder && watchedPickUpWaypointSite?.id === defaultPickUpWaypoint?.site?.id;
    if (!isInitialSavedSite && !defaultPickUpWaypoint?.contact?.name && loaded) {
      void setSiteContact(
        'pickUpWayPoint',
        watchedPickUpWaypointSite?.id,
        Boolean(defaultPickUpWaypoint?.site?.discardedAt),
      );
    }
    // Legacy deps; the v3 edit-mount guard reads (isEditingOrder + the saved
    // site id) are stable across an edit session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedPickUpWaypointSite?.id,
    defaultPickUpWaypoint?.contact?.name,
    defaultPickUpWaypoint?.site?.discardedAt,
    loaded,
  ]);

  useEffect(() => {
    // TRE-15128: mirror of the pickup guard above — a removed dropoff contact
    // must not be resurrected on edit re-open of its saved site.
    const isInitialSavedSite =
      isEditingOrder &&
      watchedDropOffWaypointSite?.id === defaultDropOffWaypoint?.site?.id;
    if (!isInitialSavedSite && !defaultDropOffWaypoint?.contact?.name && loaded) {
      void setSiteContact(
        'dropOffWayPoint',
        watchedDropOffWaypointSite?.id,
        Boolean(defaultDropOffWaypoint?.site?.discardedAt),
      );
    }
    // Legacy deps; the v3 edit-mount guard reads (isEditingOrder + the saved
    // site id) are stable across an edit session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedDropOffWaypointSite?.id,
    defaultDropOffWaypoint?.contact?.name,
    defaultDropOffWaypoint?.site?.discardedAt,
    loaded,
  ]);
}
